import React, { useState, useEffect } from 'react';
import { supabase } from '../../integrations/supabase/client';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  Send,
  ThumbsUp,
  ThumbsDown,
  History,
  Loader2
} from 'lucide-react';
import { ContratoVersionHistory } from './ContratoVersionHistory';
import { SelecionarPartesAprovacao } from './SelecionarPartesAprovacao';

interface ContratoViewerProps {
  isOpen: boolean;
  onClose: () => void;
  contratoId: string;
  processoId: string;
}

type Contrato = {
  id: string;
  processo_id: string;
  versao: number | null;
  conteudo_html: string | null;
  status: string;
  aprovacoes: any;
  comentarios: any;
  pdf_url: string | null;
  created_at: string | null;
  updated_at: string | null;
};

type Comentario = {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  texto: string;
  created_at: string;
  resolvido: boolean;
  anexos?: any[];
  replies?: any[];
};

export const ContratoViewer: React.FC<ContratoViewerProps> = ({
  isOpen,
  onClose,
  contratoId,
  processoId,
}) => {
  const { user } = useAuth();
  const { isManagerOrDirector } = usePermissions();
  const { toast } = useToast();
  
  const [contrato, setContrato] = useState<Contrato | null>(null);
  const [loading, setLoading] = useState(true);
  const [comentarioTexto, setComentarioTexto] = useState('');
  const [comentarioRejeicao, setComentarioRejeicao] = useState('');
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [isAprovador, setIsAprovador] = useState(false);
  const [meuStatusAprovacao, setMeuStatusAprovacao] = useState<string | null>(null);
  const [enviandoAprovacao, setEnviandoAprovacao] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [aprovando, setAprovando] = useState(false);
  const [rejeitando, setRejeitando] = useState(false);
  const [showSelecionarPartes, setShowSelecionarPartes] = useState(false);

  useEffect(() => {
    if (isOpen && contratoId) {
      loadContrato();
    }
  }, [isOpen, contratoId]);

  const loadContrato = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('contratos_gerados')
        .select('*')
        .eq('id', contratoId)
        .single();

      if (error) throw error;

      setContrato(data as any);

      // Verificar se usuário é aprovador
      if (data.aprovacoes && user?.id && typeof data.aprovacoes === 'object') {
        const aprovacoesObj = data.aprovacoes as Record<string, any>;
        const aprovadorKey = Object.keys(aprovacoesObj).find(
          key => key === user.id || aprovacoesObj[key]?.user_id === user.id
        );
        setIsAprovador(!!aprovadorKey);
        if (aprovadorKey && aprovacoesObj[aprovadorKey]) {
          setMeuStatusAprovacao(aprovacoesObj[aprovadorKey].status);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar contrato:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o contrato",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdicionarComentario = async () => {
    if (!comentarioTexto.trim() || !user) return;

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('user_id', user.id)
        .single();

      const novoComentario: Comentario = {
        id: crypto.randomUUID(),
        usuario_id: user.id,
        usuario_nome: profile ? `${profile.first_name} ${profile.last_name}` : 'Usuário',
        texto: comentarioTexto,
        created_at: new Date().toISOString(),
        resolvido: false,
        anexos: [],
        replies: []
      };

      const comentariosAtualizados = [...(contrato?.comentarios || []), novoComentario];

      const { error } = await supabase
        .from('contratos_gerados')
        .update({ 
          comentarios: comentariosAtualizados,
          updated_at: new Date().toISOString()
        })
        .eq('id', contratoId);

      if (error) throw error;

      setComentarioTexto('');
      loadContrato();
      
      toast({
        title: "Sucesso",
        description: "Comentário adicionado",
      });
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
      toast({
        title: "Erro",
        description: "Não foi possível adicionar o comentário",
        variant: "destructive",
      });
    }
  };

  const handleAbrirSelecaoPartes = () => {
    setShowSelecionarPartes(true);
  };

  const handleConfirmarEnvio = async (_partesSelecionadas: string[]) => {
    try {
      setEnviandoAprovacao(true);
      const { data, error } = await supabase.functions.invoke('solicitar-aprovacao-contrato', {
        body: {
          contrato_id: contratoId
        }
      });

      if (error) throw error;

      const summary = data?.summary;
      const enviados = summary?.enviados || 0;
      const erros = summary?.erros || 0;
      const total = summary?.total || 0;

      if (erros > 0) {
        toast({
          variant: "destructive",
          title: "Atenção!",
          description: `${enviados}/${total} emails enviados. ${erros} falharam. Verifique os logs.`,
        });
      } else {
        toast({
          title: "Sucesso!",
          description: `Todos os ${enviados} emails foram enviados com sucesso!`,
        });
      }
      
      loadContrato();
    } catch (error) {
      console.error('Erro ao solicitar aprovação:', error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar o contrato para aprovação",
        variant: "destructive",
      });
    } finally {
      setEnviandoAprovacao(false);
    }
  };

  const handleGerarPDF = async () => {
    try {
      setGerandoPdf(true);
      const { error } = await supabase.functions.invoke('generate-contrato-pdf', {
        body: { contrato_id: contratoId }
      });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "PDF gerado com sucesso!",
      });
      
      loadContrato();
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o PDF",
        variant: "destructive",
      });
    } finally {
      setGerandoPdf(false);
    }
  };

  const handleAprovar = async () => {
    try {
      setAprovando(true);
      const { error } = await supabase.functions.invoke('aprovar-contrato', {
        body: {
          contrato_id: contratoId,
          acao: 'aprovar'
        }
      });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Contrato aprovado",
      });
      
      loadContrato();
    } catch (error) {
      console.error('Erro ao aprovar contrato:', error);
      toast({
        title: "Erro",
        description: "Não foi possível aprovar o contrato",
        variant: "destructive",
      });
    } finally {
      setAprovando(false);
    }
  };

  const handleRejeitar = async () => {
    if (!comentarioRejeicao.trim()) {
      toast({
        title: "Atenção",
        description: "Por favor, informe o motivo da rejeição",
        variant: "destructive",
      });
      return;
    }

    try {
      setRejeitando(true);
      const { error } = await supabase.functions.invoke('aprovar-contrato', {
        body: {
          contrato_id: contratoId,
          acao: 'rejeitar',
          comentario: comentarioRejeicao
        }
      });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Contrato rejeitado",
      });
      
      setComentarioRejeicao('');
      loadContrato();
    } catch (error) {
      console.error('Erro ao rejeitar contrato:', error);
      toast({
        title: "Erro",
        description: "Não foi possível rejeitar o contrato",
        variant: "destructive",
      });
    } finally {
      setRejeitando(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      rascunho: { label: 'Rascunho', className: 'bg-gray-500' },
      em_revisao: { label: 'Em Revisão', className: 'bg-blue-500' },
      aprovado: { label: 'Aprovado', className: 'bg-green-500' },
      rejeitado: { label: 'Rejeitado', className: 'bg-red-500' }
    };

    const config = statusConfig[status] || statusConfig.rascunho;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getAprovacaoProgress = () => {
    if (!contrato?.aprovacoes) return { aprovados: 0, total: 0 };
    
    const aprovadores = Object.values(contrato.aprovacoes);
    const total = aprovadores.length;
    const aprovados = aprovadores.filter((a: any) => a.status === 'aprovado').length;
    
    return { aprovados, total };
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!contrato) return null;

  const { aprovados, total } = getAprovacaoProgress();

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-primary" />
                <DialogTitle>Contrato - Versão {contrato.versao}</DialogTitle>
                {getStatusBadge(contrato.status)}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowVersionHistory(true)}
              >
                <History className="h-4 w-4 mr-2" />
                Histórico
              </Button>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-6">
            {/* Coluna Principal - Preview do Contrato */}
            <div className="col-span-2 space-y-4">
              <div className="border rounded-lg p-6 bg-card min-h-[500px]">
                <div 
                  dangerouslySetInnerHTML={{ __html: contrato.conteudo_html || '<p>Conteúdo não disponível</p>' }}
                  className="prose prose-sm max-w-none"
                />
              </div>

              {/* Ações */}
              <div className="flex gap-2 flex-wrap">
                {contrato.status === 'rascunho' && isManagerOrDirector() && (
                  <Button onClick={handleAbrirSelecaoPartes} disabled={enviandoAprovacao}>
                    {enviandoAprovacao ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    {enviandoAprovacao ? 'Enviando...' : 'Enviar para Aprovação'}
                  </Button>
                )}

                {contrato.status === 'em_revisao' && isAprovador && meuStatusAprovacao === 'pendente' && (
                  <>
                    <Button 
                      onClick={handleAprovar} 
                      className="bg-green-600 hover:bg-green-700"
                      disabled={aprovando}
                    >
                      {aprovando ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ThumbsUp className="h-4 w-4 mr-2" />
                      )}
                      {aprovando ? 'Aprovando...' : 'Aprovar'}
                    </Button>
                    <Button 
                      variant="destructive" 
                      disabled={rejeitando}
                      onClick={() => {
                        const motivo = prompt('Informe o motivo da rejeição:');
                        if (motivo) {
                          setComentarioRejeicao(motivo);
                          handleRejeitar();
                        }
                      }}
                    >
                      {rejeitando ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ThumbsDown className="h-4 w-4 mr-2" />
                      )}
                      {rejeitando ? 'Rejeitando...' : 'Rejeitar'}
                    </Button>
                  </>
                )}

                {contrato.status === 'em_revisao' && aprovados === total && total > 0 && isManagerOrDirector() && !contrato.pdf_url && (
                  <Button 
                    onClick={handleGerarPDF} 
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={gerandoPdf}
                  >
                    {gerandoPdf ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    {gerandoPdf ? 'Gerando PDF...' : 'Gerar PDF Final'}
                  </Button>
                )}

                {contrato.pdf_url && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      if (contrato.pdf_url) {
                        window.open(contrato.pdf_url, '_blank');
                      }
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Baixar PDF
                  </Button>
                )}
              </div>
            </div>

            {/* Coluna Lateral - Status e Comentários */}
            <div className="space-y-4">
              {/* Progresso de Aprovações */}
              {contrato.status === 'em_revisao' && (
                <div className="border rounded-lg p-4 bg-card">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Progresso de Aprovações
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Aprovações</span>
                      <span className="font-semibold">{aprovados}/{total}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${total > 0 ? (aprovados / total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Status de Aprovações Detalhado */}
              {contrato.aprovacoes && Object.keys(contrato.aprovacoes).length > 0 && (
                <div className="border rounded-lg p-4 bg-card">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Status das Partes
                  </h3>
                  
                  <div className="space-y-2">
                    {Object.entries(contrato.aprovacoes).map(([key, aprovador]: [string, any]) => (
                      <div key={key} className="flex items-center justify-between p-2 bg-muted rounded">
                        <div className="flex items-center gap-2">
                          {aprovador.status === 'aprovado' && <CheckCircle className="h-4 w-4 text-green-500" />}
                          {aprovador.status === 'rejeitado' && <XCircle className="h-4 w-4 text-red-500" />}
                          {aprovador.status === 'pendente' && <Clock className="h-4 w-4 text-yellow-500" />}
                          <span className="text-sm font-medium">{aprovador.nome}</span>
                        </div>
                        <Badge variant={
                          aprovador.status === 'aprovado' ? 'default' : 
                          aprovador.status === 'rejeitado' ? 'destructive' : 
                          'secondary'
                        }>
                          {aprovador.status === 'aprovado' ? 'Aprovado' : 
                           aprovador.status === 'rejeitado' ? 'Rejeitado' : 
                           'Pendente'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PDF Gerado */}
              {contrato.pdf_url && (
                <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                  <h3 className="font-semibold mb-2 flex items-center gap-2 text-green-700 dark:text-green-300">
                    <CheckCircle className="h-4 w-4" />
                    PDF Aprovado
                  </h3>
                  <p className="text-sm text-green-600 dark:text-green-400 mb-3">
                    O contrato foi aprovado por todas as partes e o PDF está disponível.
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      if (contrato.pdf_url) {
                        window.open(contrato.pdf_url, '_blank');
                      }
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Visualizar PDF
                  </Button>
                </div>
              )}

              {/* Comentários */}
              <div className="border rounded-lg p-4 bg-card">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Comentários ({contrato.comentarios?.length || 0})
                </h3>

                <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                  {contrato.comentarios?.map((comentario: Comentario) => (
                    <div key={comentario.id} className="p-3 bg-muted rounded-lg">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-sm">{comentario.usuario_nome}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(comentario.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <p className="text-sm">{comentario.texto}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <textarea
                    value={comentarioTexto}
                    onChange={(e) => setComentarioTexto(e.target.value)}
                    placeholder="Adicione um comentário..."
                    className="w-full p-2 border rounded-md min-h-[80px] resize-none"
                  />
                  <Button 
                    onClick={handleAdicionarComentario}
                    disabled={!comentarioTexto.trim()}
                    size="sm"
                    className="w-full"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Enviar Comentário
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SelecionarPartesAprovacao
        isOpen={showSelecionarPartes}
        onClose={() => setShowSelecionarPartes(false)}
        onConfirm={handleConfirmarEnvio}
        processoId={processoId}
      />

      {showVersionHistory && (
        <ContratoVersionHistory
          isOpen={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
          processoId={processoId}
          currentVersion={contrato.versao || 1}
        />
      )}
    </>
  );
};